import apiClientService from "./ApiClientService";

// Balanța elevului. Sumele vin în bani (1 leu = 100 bani), numere întregi.
// GET  /balance/config                 -> { enabled, cardTopupEnabled, currency, topupMinBani, topupMaxBani }
// GET  /balance/me                     -> { userId, balanceBani, entries: [{ id, amountBani, balanceAfterBani, type, reason, paymentId, createdAt }], nextBeforeId }
// GET  /balance/users/:userId          -> la fel + entries[].createdBy { id, name } (ADMIN, MANAGER)
// POST /balance/users/:userId/adjust   { amountBani, type: TOPUP_MANUAL | CORRECTION, reason } (ADMIN)

async function readOrThrow(res, fallback) {
   if (res.ok) return res.json();
   let msg = fallback;
   try {
      const err = await res.json();
      if (err?.message) msg = Array.isArray(err.message) ? err.message.join(" ") : err.message;
   } catch {}
   throw new Error(msg);
}

const pageQuery = ({ beforeId, limit } = {}) => {
   const p = new URLSearchParams();
   if (beforeId) p.set("beforeId", String(beforeId));
   if (limit) p.set("limit", String(limit));
   const qs = p.toString();
   return qs ? `?${qs}` : "";
};

export async function getBalanceConfig() {
   return readOrThrow(await apiClientService.get("/balance/config"), "Nu am putut încărca setările balanței.");
}

export async function getMyBalance(page) {
   return readOrThrow(await apiClientService.get(`/balance/me${pageQuery(page)}`), "Nu am putut încărca balanța.");
}

export async function getUserBalance(userId, page) {
   return readOrThrow(
      await apiClientService.get(`/balance/users/${encodeURIComponent(userId)}${pageQuery(page)}`),
      "Nu am putut încărca balanța elevului.",
   );
}

export async function adjustUserBalance(userId, { amountBani, type, reason }) {
   return readOrThrow(
      await apiClientService.post(`/balance/users/${encodeURIComponent(userId)}/adjust`, { amountBani, type, reason }),
      "Nu am putut salva operația.",
   );
}

/** 12345 bani -> "123,45 lei" */
export function formatLei(bani) {
   const n = Number(bani) || 0;
   const sign = n < 0 ? "−" : "";
   const abs = Math.abs(n);
   const lei = Math.floor(abs / 100);
   const rest = String(abs % 100).padStart(2, "0");
   return `${sign}${lei.toLocaleString("ro-RO")},${rest} lei`;
}

/** 12345 bani -> { lei: "123", cents: "45" }; cents gol la suma întreagă (fără „,00”). */
export function splitLei(bani) {
   const n = Number(bani) || 0;
   const abs = Math.abs(n);
   const rest = abs % 100;
   return {
      lei: `${n < 0 ? "−" : ""}${Math.floor(abs / 100).toLocaleString("ro-RO")}`,
      cents: rest ? String(rest).padStart(2, "0") : "",
   };
}

/** 50000 -> "500 lei", 50050 -> "500,50 lei" */
export function formatLeiShort(bani) {
   const { lei, cents } = splitLei(bani);
   return cents ? `${lei},${cents} lei` : `${lei} lei`;
}

/** Felul unei operații din istoria balanței, pentru elev. */
export const BALANCE_TYPE_LABELS = {
   TOPUP_CARD: "Alimentare cu cardul",
   TOPUP_MANUAL: "Alimentare",
   CORRECTION: "Corectare",
   PURCHASE: "Cumpărare",
   REFUND: "Rambursare",
};

/** "123,45" / "123.45" / "123" -> 12345 bani; null dacă nu e o sumă validă (cel mult 2 zecimale). */
export function parseLeiToBani(text) {
   const s = String(text ?? "").trim().replace(/\s/g, "").replace(",", ".");
   if (!/^-?\d+(\.\d{1,2})?$/.test(s)) return null;
   const [int, frac = ""] = s.replace("-", "").split(".");
   const bani = Number(int) * 100 + Number(frac.padEnd(2, "0"));
   return s.startsWith("-") ? -bani : bani;
}
